import { useCallback, useMemo, useState } from "react";

import { isValidCoordinatePair } from "../utils/shared/coordinates";

type UseMapCoordinateInputsArgs = {
	/** Secondary camera / initial-viewport fallback (e.g. location coordinates). */
	fallbackCenter?: [number, number] | null;
	latitude: string;
	longitude: string;
	/** Primary camera / initial-viewport fallback (e.g. parent map center). */
	mapCenter?: [number, number] | null;
};

const asValidCenter = (
	center: [number, number] | null | undefined,
): [number, number] | undefined =>
	isValidCoordinatePair(center ?? undefined)
		? (center as [number, number])
		: undefined;

/**
 * Shared lat/lng parsing, partial-coordinate detection, map frame center,
 * and viewport tracking for create/edit map placement forms.
 */
export const useMapCoordinateInputs = ({
	fallbackCenter,
	latitude,
	longitude,
	mapCenter,
}: UseMapCoordinateInputsArgs) => {
	const parsedCoordinates = useMemo((): [number, number] | undefined => {
		const lat = parseFloat(latitude);
		const lng = parseFloat(longitude);
		if (isValidCoordinatePair([lng, lat])) return [lng, lat];
		return undefined;
	}, [latitude, longitude]);

	const hasPartialCoordinates =
		(latitude.trim() !== "" || longitude.trim() !== "") && !parsedCoordinates;

	const mapFrameCenter = useMemo((): [number, number] | undefined => {
		if (parsedCoordinates) return parsedCoordinates;
		return asValidCenter(mapCenter) ?? asValidCenter(fallbackCenter);
	}, [fallbackCenter, mapCenter, parsedCoordinates]);

	const [viewportCenter, setViewportCenter] = useState<[number, number] | null>(
		() => asValidCenter(mapCenter) ?? asValidCenter(fallbackCenter) ?? null,
	);

	const onMoveEnd = useCallback((center: [number, number]) => {
		setViewportCenter(center);
	}, []);

	return {
		hasPartialCoordinates,
		mapFrameCenter,
		onMoveEnd,
		parsedCoordinates,
		viewportCenter,
	};
};
